import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { JwtAuthService } from '../services/auth/jwt-auth.service';
import { Observable, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthStatements } from '../../state/models/auth-statements';
import { OrganizationEntityService } from '../../state/entity-services/organization-entity.service';
import { OrganizationService } from '../services/organization.service';
import { AmplitudeService } from '../services/amplitude.service';
import { Person } from '../../state/models/person';
import { DateService } from '../services/date.service';
import { FeatureFlagService } from '../services/feature-flag.service';
import { AclService } from '../services/acl.service';

@Injectable()
export class OrganizationListGuard implements CanActivate {
  constructor(
    private aclService: AclService,
    private featureFlagService: FeatureFlagService,
    private dateService: DateService,
    private jwtAuthService: JwtAuthService,
    private amplitudeService: AmplitudeService,
    private organizationService: OrganizationService,
    private organizationEntityService: OrganizationEntityService,
    private router: Router
  ) {
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    if (!this.jwtAuthService.isLoggedIn || !this.jwtAuthService.getUser()?.id) {
      this.redirectToLogin(state.url);
      return of(false);
    }
    return this.aclService.getRoles(this.jwtAuthService.getUser()?.id).pipe(
      switchMap(response => {
        const { permissions, authStatements } = response;
        return this.handleAuthStatements(authStatements, permissions);
      })
    );
  }

  private redirectToLogin(returnUrl: string): void {
    this.router.navigate(['/sessions/login'], { queryParams: { return: returnUrl } });
  }

  private handleAuthStatements(authStatements: AuthStatements[], permissions: { name }[]): Observable<boolean> {
    this.organizationService.setOrganization(null);
    const user = this.jwtAuthService.getUser();

    this.trackUserEvents(user, authStatements);

    if (this.isAdmin(permissions)) {
      this.organizationService.setInfoPermissions({ hasAdmin: true }, authStatements);
      return of(true);
    }

    const orgIds = this.getUniqueOrgIds(authStatements);

    if (orgIds.length > 1) {
      this.organizationService.setInfoPermissions({ hasManyOrgs: true }, authStatements);
      return of(true);
    }
    return this.loadOrganization(orgIds[0], authStatements);
  }

  private isAdmin(permissions: { name } []): boolean {
    return permissions.some(as => as.name === 'ALL_ORGANIZATIONS');
  }

  private getUniqueOrgIds(authStatements: AuthStatements[]): number[] {
    return authStatements
      .filter(as => ['MANAGER', 'DOCTOR'].includes(as.role.name))
      .map(as => as?.resource?.organization?.id)
      .filter((id, index, self) => id && self.indexOf(id) === index);
  }

  private trackUserEvents(user: Person, authStatements: AuthStatements[]): void {
    this.amplitudeService.setUserId(user.id.toString());

    const { birthdate, biologicalSex } = user.personProperties;
    this.amplitudeService.addEdentifyEvent('biological_sex', biologicalSex);
    this.amplitudeService.addEdentifyEvent('age', this.dateService.diffByYears(birthdate).toString());

    authStatements.forEach(as => {
      this.amplitudeService.addEdentifyEvent('user_type', as.role.name);
    });
  }

  private loadOrganization(organizationId: number, authStatements: AuthStatements[]): Observable<boolean> {
    if (!organizationId) {
      return of(false);
    }

    this.organizationService.setInfoPermissions({ hasManyOrgs: false, hasAdmin: false }, authStatements);
    return this.organizationEntityService.getWithQuery({ idsOrRelatedOrgIds: organizationId }).pipe(
      switchMap(organizations => {
          if (organizations.length > 1) {
            this.organizationService.setInfoPermissions({ hasManyOrgs: true }, authStatements);
            return of(true);
          }
          return this.featureFlagService.getFeatureFlagSearchesByOrgId(organizations[0].id).pipe(
            map(() => organizations[0])
          );
        }
      ),
      map(response => {

        if (typeof response === 'boolean') {
          return true;
        }

        this.organizationService.setOrganization(response);
        this.router.navigateByUrl(this.organizationService.getDefaultUrl());
        return true;
      })
    );
  }
}
